import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Alert,
  TextField,
  CircularProgress,
} from "@mui/material";
import { SyncAlt } from "@mui/icons-material";
import { useOdooMigrationPermissions } from "../../hooks/useOdooMigrationPermissions";
import { odooProductionMigrationService } from "../../services/odooProductionMigrationService";
import type { TriggerProductionMigrationDto } from "../../types/odoo-migration";

interface OdooProductionMigrationButtonProps {
  onMigrationStarted?: () => void;
  onMigrationError?: (error: string) => void;
}

export function OdooProductionMigrationButton({
  onMigrationStarted,
  onMigrationError,
}: OdooProductionMigrationButtonProps) {
  const { canExecuteManual, isLoading } = useOdooMigrationPermissions();
  const [dialogStep, setDialogStep] = useState<"closed" | "confirm" | "params">(
    "closed",
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [formData, setFormData] = useState<TriggerProductionMigrationDto>({
    reason: "",
  });

  // No renderizar si no tiene permisos o esta cargando
  if (isLoading || !canExecuteManual) {
    return null;
  }

  const handleOpenDialog = () => {
    setDialogStep("confirm");
  };

  const handleCloseDialog = () => {
    setDialogStep("closed");
    setFormData({
      reason: "",
    });
  };

  const handleContinueToParams = () => {
    setDialogStep("params");
  };

  const handleExecuteMigration = async () => {
    if (!formData.reason.trim()) {
      return;
    }

    setIsExecuting(true);
    try {
      const response =
        await odooProductionMigrationService.triggerManualMigration(formData);

      if (response.success) {
        onMigrationStarted?.();
        handleCloseDialog();
      } else {
        onMigrationError?.(response.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      onMigrationError?.(errorMessage);
    } finally {
      setIsExecuting(false);
    }
  };

  const isFormValid = formData.reason.trim().length >= 10;

  return (
    <>
      {/* Boton de sincronizacion manual */}
      <Button
        variant="contained"
        color="warning"
        size="small"
        onClick={handleOpenDialog}
        startIcon={<SyncAlt />}
        sx={{ fontWeight: "bold", px: 3, py: 1 }}
      >
        Iniciar Sincronizacion Manual
      </Button>

      {/* Paso 1 — Confirmacion */}
      <Dialog
        open={dialogStep === "confirm"}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Confirmar Sincronizacion Manual
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Se ejecutara una copia de la base de datos de produccion Odoo
              (VillApps) hacia la copia local (odoo_18). Este proceso:
            </Typography>
            <Stack spacing={0.5} sx={{ ml: 2 }}>
              <Typography variant="body2">
                1. Crea un respaldo (pg_dump) en el servidor de produccion (solo
                lectura)
              </Typography>
              <Typography variant="body2">
                2. Descarga el respaldo comprimido via SCP
              </Typography>
              <Typography variant="body2">
                3. Reemplaza la base de datos local odoo_18 con los datos
                descargados
              </Typography>
              <Typography variant="body2">
                4. Limpia archivos temporales
              </Typography>
            </Stack>
            <Alert severity="info" variant="outlined">
              El servidor de produccion no se modifica. Solo se lee para generar
              el respaldo.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">
            Cancelar
          </Button>
          <Button
            onClick={handleContinueToParams}
            variant="contained"
            color="primary"
            size="small"
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paso 2 — Parametros de ejecucion */}
      <Dialog
        open={dialogStep === "params"}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Justificacion de la Sincronizacion
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Razon de la sincronizacion manual"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="Ejemplo: Se necesitan datos actualizados de inventario para reporte..."
              required
              error={
                formData.reason.trim().length > 0 &&
                formData.reason.trim().length < 10
              }
              helperText="Obligatorio para auditoria (minimo 10 caracteres)"
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">
            Cancelar
          </Button>
          <span>
            <Button
              onClick={handleExecuteMigration}
              variant="contained"
              color="primary"
              disabled={!isFormValid || isExecuting}
              startIcon={
                isExecuting ? <CircularProgress size={16} /> : <SyncAlt />
              }
              size="small"
            >
              {isExecuting ? "Ejecutando..." : "Ejecutar Sincronizacion"}
            </Button>
          </span>
        </DialogActions>
      </Dialog>
    </>
  );
}
